import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { treatmentApi } from 'src/api/treatment';
import Iconify from 'src/components/iconify';
import {
  TreatmentPdfItem,
  TreatmentsSummaryPDF,
} from 'src/components/treatments-summary-pdf';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { useTreatmentsScope } from 'src/sections/@dashboard/visit/hooks/useTreatmentsScope';
import { useVisitScope } from 'src/sections/@dashboard/visit/hooks/useVisitScope';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import {
  AddProfileTreatmentDto,
  UpdateProfileTreatmentDto,
} from 'woofwoof-api';
import ProfileTreatmentDeleteModal from './ProfileTreatmentDeleteModal';
import ProfileTreatmentFormModal, {
  TreatmentFormSubmitValues,
} from './ProfileTreatmentFormModal';
import ProfileTreatmentsTable from './ProfileTreatmentsTable';
import { TreatmentFormSnapshot } from './treatmentFormValues';
import ProfileTreatmentsTableSkeleton from './ProfileTreatmentsTableSkeleton';
import { translateKey } from 'src/locales/translate';
import { forbiddenMessage } from 'src/utils/forbiddenMessage';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export function ProfileTreatmentsContent() {
  const theme = useTheme();
  const {
    selectedOrganization,
    isProfessionalRoleSelected,
    isOrganizationAdminRoleSelected,
  } = useOrganizationContext();
  const { visitId, isVisitScoped } = useVisitScope();
  const isOrgAdmin = isOrganizationAdminRoleSelected ?? false;
  const isProfessional = isProfessionalRoleSelected ?? false;
  const canManage = isProfessional || (!isOrgAdmin && !isProfessional);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [treatmentId, setTreatmentId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllAnchor, setSelectAllAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [isExporting, setIsExporting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const writeError = useStockWriteError();

  const handleOnRowClick = (id: string) => {
    setTreatmentId(id);
    setIsModalOpen(true);
  };

  const handleOpenModal = () => {
    setTreatmentId('');
    setIsModalOpen(true);
  };

  const handleOnCloseModal = () => {
    setIsModalOpen(false);
    setTreatmentId('');
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const { data: catalogData } = useQuery(
    ['treatment-catalog', selectedOrganization?.id],
    () =>
      treatmentApi.treatmentControllerGetCatalog({
        organizationId: selectedOrganization!.id,
      }),
    {
      enabled: Boolean(
        (isProfessional || isOrgAdmin) && selectedOrganization?.id,
      ),
      staleTime: 5 * 60 * 1000,
    },
  );

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  const SUCCESS_KEYS = {
    create: 'snackbar.treatmentAdded',
    update: 'snackbar.treatmentUpdated',
    delete: 'snackbar.treatmentDeleted',
  } as const;

  const ERROR_KEYS = {
    create: 'snackbar.treatmentAddError',
    update: 'snackbar.treatmentUpdateError',
    delete: 'snackbar.treatmentDeleteError',
  } as const;

  const {
    items: treatments,
    total,
    isLoading,
    create: addTreatment,
    update: updateTreatmentInScope,
    remove: removeTreatment,
    fetchAll: fetchAllTreatments,
    isCreating,
    isUpdating,
    isDeleting,
    createPending,
    updatePending,
    createBlocked,
    updateBlocked,
    createFrozenValues,
    updateFrozenValues,
  } = useTreatmentsScope({
    page,
    rowsPerPage,
    editingId: treatmentId,
    onMutationSuccess: (kind, variables) => {
      enqueueSnackbar(translateKey(SUCCESS_KEYS[kind]), { variant: 'success' });
      if (kind === 'create') {
        const medCount = variables?.associatedMedicines?.length ?? 0;
        if (medCount > 0) {
          enqueueSnackbar(
            translateKey('snackbar.medications_added_from_treatment', {
              count: medCount,
            }),
            { variant: 'info' },
          );
        }
      }
      setIsModalOpen(false);
      if (kind === 'delete') setIsDeleteModalOpen(false);
      if (kind !== 'create') setTimeout(() => setTreatmentId(''), 500);
    },
    // A Warehouse refusal, else the backend's reason for a 403 (#707).
    onMutationError: (kind, error) =>
      writeError(forbiddenMessage(error) || translateKey(ERROR_KEYS[kind]))(
        error,
      ),
  });

  const selectedTreatment = treatmentId
    ? treatments.find((t) => t.id === treatmentId)
    : undefined;

  const canEditSelectedTreatment = selectedTreatment?.canEdit ?? true;

  const handleOnSubmit = useCallback(
    (values: TreatmentFormSubmitValues, shown: TreatmentFormSnapshot) => {
      if (treatmentId && !canEditSelectedTreatment) return;
      if (treatmentId) {
        updateTreatmentInScope(
          treatmentId,
          values as UpdateProfileTreatmentDto,
          shown,
        );
      } else {
        addTreatment(
          {
            ...values,
            ...(selectedOrganization?.id
              ? { organizationId: selectedOrganization.id }
              : {}),
          } as AddProfileTreatmentDto,
          shown,
        );
      }
    },
    [
      canEditSelectedTreatment,
      addTreatment,
      treatmentId,
      selectedOrganization,
      updateTreatmentInScope,
    ],
  );

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectPage = useCallback(
    (ids: string[], checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
        return next;
      });
    },
    [],
  );

  const handleToggleSelectAll = useCallback(
    async (checked: boolean) => {
      if (!checked) {
        setSelectedIds(new Set());
        return;
      }
      // Selecting "all" means all of them, not the page on screen.
      const all = await fetchAllTreatments();
      setSelectedIds(new Set(all.map((t) => t.id)));
    },
    [fetchAllTreatments],
  );

  const handleExportPdf = async () => {
    // The selection can span pages, so the rows behind it are fetched rather
    // than read off the table.
    const allTreatments = await fetchAllTreatments();
    const selectedTreatments: TreatmentPdfItem[] = allTreatments
      .filter((t) => selectedIds.has(t.id))
      .map((t) => ({
        name: t.name,
        treatmentDate: t.treatmentDate,
        priceNetto: t.priceNetto,
        vatRate: t.vatRate,
        priceBrutto: t.priceBrutto,
      }));

    if (selectedTreatments.length === 0) return;

    setIsExporting(true);
    try {
      const blob = await pdf(
        <TreatmentsSummaryPDF
          treatments={selectedTreatments}
          showPatientColumn={false}
          organizationName={selectedOrganization?.name}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${translateKey(
        'hardcoded.treatmentSummaryFilename',
      )} ${format(new Date(), 'dd.MM.yyyy HH.mm', { locale: pl })}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar(translateKey('snackbar.pdfGenerateError'), {
        variant: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Selecting rows exists to export a summary of a patient's treatments, which
   * belongs on the patient's own Zabiegi page. Inside a visit the professional
   * is recording that visit, not compiling a report, so the checkboxes, the
   * select-all menu and the PDF button stay off there.
   */
  const canSelectRows = !visitId;
  const hasSelection = canSelectRows && selectedIds.size > 0;

  return (
    <>
      <Stack sx={CARD_HEADER_SX}>
        {isLoading ? (
          <Skeleton variant="rounded" height="24px" width="56px" />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography {...CARD_HEADER_TITLE_PROPS}>
              {translateKey('visit.tabTreatments')}
            </Typography>
            {hasSelection && (
              <>
                <Typography variant="body2" color="text.secondary">
                  ({selectedIds.size} zaznaczono)
                </Typography>
                <Tooltip title="Opcje zaznaczania">
                  <IconButton
                    size="small"
                    onClick={(e) => setSelectAllAnchor(e.currentTarget)}
                  >
                    <Iconify icon="mdi:checkbox-multiple-marked-outline" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={selectAllAnchor}
                  open={Boolean(selectAllAnchor)}
                  onClose={() => setSelectAllAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setSelectAllAnchor(null);
                      handleToggleSelectAll(true);
                    }}
                  >
                    Zaznacz wszystkie ({total})
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectAllAnchor(null);
                      handleToggleSelectAll(false);
                    }}
                  >
                    Odznacz wszystkie
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        )}
        {isLoading ? (
          <Skeleton variant="rounded" height="30px" width="77px" />
        ) : (
          <Stack direction="row" spacing={1}>
            {canManage && (
              <Button
                size="small"
                startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                data-testid="treatment-add-open"
                variant={hasSelection ? 'outlined' : 'contained'}
                color={hasSelection ? 'inherit' : undefined}
                sx={
                  !hasSelection
                    ? {
                        background: theme.palette.common.black,
                        color: theme.palette.common.white,
                      }
                    : {}
                }
                onClick={handleOpenModal}
              >
                {translateKey('common.add')}
              </Button>
            )}
            {hasSelection && (
              <Button
                onClick={handleExportPdf}
                variant="contained"
                disabled={isExporting}
                sx={{
                  background: theme.palette.common.black,
                  color: theme.palette.common.white,
                }}
                size="small"
                startIcon={<Iconify icon="mdi:file-pdf-box" width={16} />}
              >
                {isExporting ? 'Generowanie...' : 'Eksportuj PDF'}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {isLoading ? (
        <ProfileTreatmentsTableSkeleton />
      ) : (
        <ProfileTreatmentsTable
          data={treatments}
          enableSelection={canSelectRows}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          onRowClick={handleOnRowClick}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleSelectPage={handleToggleSelectPage}
        />
      )}

      <ProfileTreatmentFormModal
        isModalOpen={isModalOpen}
        closeModal={handleOnCloseModal}
        onSubmit={handleOnSubmit}
        onDelete={handleOpenDeleteModal}
        // A clinic's write keeps row stock, whichever role; a vet edits it.
        stockEnabled={Boolean(selectedOrganization?.id)}
        stockEditable={isProfessional}
        unconfirmedSave={treatmentId ? updatePending : createPending}
        unconfirmedBlocked={treatmentId ? updateBlocked : createBlocked}
        unconfirmedValues={
          treatmentId ? updateFrozenValues : createFrozenValues
        }
        treatment={selectedTreatment as any}
        canEdit={canManage && canEditSelectedTreatment}
        isViewOnly={isOrgAdmin && !isProfessional}
        isProfessional={isProfessional || isOrgAdmin}
        organizationId={selectedOrganization?.id}
        catalogOptions={(catalogData?.data as any) ?? []}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        singleReminderOnly={isVisitScoped}
      />

      <ProfileTreatmentDeleteModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseDeleteModal}
        onDelete={() => removeTreatment(treatmentId)}
        isLoading={isDeleting}
      />
    </>
  );
}
